import {DeviceProvide} from "@hybird/core/device/provide";
import {
  BleCodec,
  BleDeviceProtocol,
  BleDeviceProtocolBeginSeal,
  BleDeviceProtocolOperation,
  BleSend
} from "@hybird/device/protocol/ble";
import {config, DeviceType, setConfig} from "@hybird/device/config";
import {defaultResult as response, DeviceEvent} from "@hybird/core/device/protocol";
import service from "@hybird/core/request";
import url from "@hybird/device/request";
import request from "@hybird/device/request";
import {Buffer} from "buffer";
import {WebError} from "@hybird/core/error";
import {deviceError} from "@hybird/device/deviceError";
import moment from "moment"

class SimpleDeviceProvide extends DeviceProvide {

  _deviceProtocols = {
    1: SimpleBleDeviceProtocol,
    3: SimpleBleDeviceProtocol,
  };

  constructor(deviceConnectProvide) {
    super(deviceConnectProvide);
  }

  isSupport(deviceInfo) {
    return DeviceType.simple === deviceInfo.modelType;
  }
}

class SimpleBleDeviceProtocol extends BleDeviceProtocol {

  constructor(deviceProvide, deviceInfo, deviceProtocolOption) {
    super(deviceProvide, deviceInfo, deviceProtocolOption, SimpleSend);
  }

  get codec() {
    return BleSimpleCodec.codec;
  }

  get serviceName() {
    return {
      DEVICE_MATCH: "DEVICEMATCH",
      MAINTAIN: "MAINTAIN",
      LOCK: "LOCK",
      MOVE: "MOVE",
      REMOTE_LOCK: "REMOTELOCK",
      STICK_OUT_STAMP: "STICKOUTSTAMP",
      STOP_STAMP: "STOPSTAMP",
      STOP_REPLACE: "STOPREPLACE",
      STOP_AINK: "STOPAINK",
      EXAMINE_RESULT: "EXAMINERESULT",
      FINGERPRINT: "FIGRESULT",
      START_STAMP: "STARTSTAMP",
      REPLY_STAMP: "REPLYSTAMP",
      POST_DOCUMENT_FILEID: "POSTDOCUMENTFILEID",
      INKING: "INKING",
      ENVINFO: "ENVINFOV2",
      AINK: "AINK",
      AINK_END: "AINKEND",
      START_REPLACE: "STARTREPLACE",
      END_REPLACE: "ENDREPLACE",
      FIG: "FIG",
      FIGSYNC: "FIGSYNC",
      FEATUREPACKET: "FEATUREPACKET",

      UPGRADE: "UPGRADE",
      PACKET: "PACKET",
      WARN: "WARN"

    }
  }

  async _connect({success, complete, message, fail, timeout} = {}) {
    this.emit(DeviceEvent.BEFORE_USER_LOGIN);

    this.deviceConnectProvide.codec = this.codec;

    await this.syncTime();

    const result = await service.post(url.userLogin, {
      model: 1,
      sessionId: this.deviceInfo.sessionId,
      deviceType: config.deviceType,
      number: this.deviceInfo.number,
      gps: config.gps
    }, {timeout: timeout * 1000});

    this.emit(DeviceEvent.USER_LOGIN);
  }

  async _disconnect({type, logout, success, complete, message, fail, timeout} = {}) {
    try {
      await service.put(url.userLogout, {
        number: this.deviceInfo.number,
        sessionId: this.deviceInfo.sessionId
      })

      if (this.deviceConnectProvide.connected && this.deviceProtocolBeginSeal) {
        await this.deviceProtocolBeginSeal.endSeal({force: true, type, logout});
      }

      await this.send.unsubscribe();
      await success?.call(this);
      complete?.call(this);
    } catch (e) {
      complete?.call(this, null, e);
    }
  }

  async _precord() {
    const cacheTopic = {}
    return new Promise((resolve, reject) => {
      const list = [];
      this.send.listen("PRECORD", {
        cacheTopic: cacheTopic,
        message: (data) => {
          const totalPage = parseInt(data.params[1]);

          console.log(data);
          const dataList = data.extra.split("#");

          for (let i = 0; i < dataList.length; i++) {
            const data = dataList[i];
            if (!data) {
              continue;
            }
            const item = data.split('~');

            list.push({
              'number': this.deviceInfo.number,
              'position': item[0],
              'sealId': item[1],
              'sealTime': item[3],
              'specialTag': item[4]
            });
          }

          if (list.length >= totalPage) {
            const success = () => {
              this.emit(DeviceEvent.SYNC_STATUS, {progress: 1, type: 2, data: list});
              resolve();
            }

            if (list.length > 0) {
              service.post(request.privilegeUpload, list).then(data => {
                success();
              }).catch(reject);
            } else {
              success();
            }
          } else {
            this.emit(DeviceEvent.SYNC_STATUS, {progress: list.length / totalPage, type: 2, data: list});
          }
        }
      })

      this.send.service("PRECORD", [], {noReply: true})
    }).finally(()=>{
      this.send.unsubscribe(cacheTopic);
    });
  }

  async figsync({type, position, auth, featureString, success, complete, message, fail, timeout} = {}) {
    position = position.toString().padLeft(3, 0);

    await this.send.service(this.serviceName.FIGSYNC, [type, position, auth], {
      success,
      complete,
      message,
      fail,
      timeout
    }).then(async () => {
      if (type === 1) {
        const buffer = Buffer.from(featureString, "hex");
        const length = buffer.length;
        const size = Math.ceil(length / 6);

        await this.sendPackage({
          type: 0x02,
          size,
          buffer,
          fn: async (i, data, size) => {
            await this.send.service(this.serviceName.FEATUREPACKET, [data], {
              success,
              complete,
              message,
              fail,
              timeout
            });
          }
        })
      }
    });

    await service.put(url.fingerprintImportState, {
      number: this.deviceInfo.number,
      type, position
    })
  }

  async sendPackage({type = 0x01, buffer, start, fn, size = 512}) {
    const totalPageSize = Math.ceil(buffer.length / size);

    return super.sendPackage({
      buffer, start, fn: async (i, data, size) => {
        const msg = Buffer.allocUnsafe(2048);

        let index = 0;
        Buffer.from([0xef, type, 0xff, 0xff, 0xff, 0xff]).copy(msg, index);

        Buffer.from([Math.floor(i / 256), Math.floor(i % 256)]).copy(msg, (index += 6));
        Buffer.from([Math.floor(data.length / 256), Math.floor(data.length % 256)]).copy(msg, (index += 2));
        data.copy(msg, (index += 2));
        index += data.length
        let sum = 0;
        for (let i = 0; i < index; i++) {
          sum += msg[i];
        }
        Buffer.from([Math.floor(sum / 256), Math.floor(sum % 256)]).copy(msg, (index));
        index += 2;

        await fn?.call(this, i, msg.slice(0, index), size);
      }, size
    });
  }

  _createDeviceProtocolBeginSeal(option) {
    return new SimpleBleDeviceProtocolBeginSeal(this.deviceInfo, this, option);
  }

  _createDeviceProtocolOperation() {
    return new SimpleBleDeviceProtocolOperation(this.deviceInfo, this);
  }

  async syncTime() {
    const params = await this.send.service("SYNC", [moment().format("YYYY-MM-DD HH:mm:ss")]);

    this._power = parseInt(params.params[2]);
    if (params.params[1] === "1") {
      throw WebError.code(deviceError.PRIVILEGED_SEAL_USAGE);
    }

    await service.put(request.propertySync, {
      'number': this.deviceInfo.number, //设备编号
      'energyType': parseInt(params.params[3]),
      'energy': this._power,
      'firmwareEdition': params.params.length > 5 ? params.params[5] : ""
    }).catch(e => {
      console.warn("syncTime", "同步失败", e);
    })
  }

  getPower() {
    return this._power;
  }
}

class SimpleSend extends BleSend {

  async cancelOperation({type, success, complete, message, fail, timeout} = {}) {
    if (type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0 && this.deviceProtocol.deviceInfo.sealAuthentication !== 1) {
      return;
    }

    return this.service("CANCEL", [type], {noReply: true, success, complete, message, fail, timeout});
  }

  async init() {
    this.deviceProtocol.on(DeviceEvent.CONNECTED, async () => {
      await this.listen("TIMEOUT", {
        cacheTopic: false,
        message: () => {
          this.deviceProtocol.emit(DeviceEvent.TIMEOUT);
          this.deviceProtocol.endSeal({force: true, logout: true, type: 2});
        }
      })

      await this.listen(DeviceEvent.DISCONNECTED, {
        message: data => {
          this.deviceProtocol.emit(DeviceEvent.OFFLINE, data);
          this.deviceProtocol.disconnect({force: true, logout: true, type: 3, connectType: 1});
          this.clearKeepAlive?.();
          this.deviceProtocol.online = false;
        }
      });
    });
  }

  createKeepAlive() {
    this.clearKeepAlive = () => {
    };
  }
}

class SimpleBleDeviceProtocolBeginSeal
        extends BleDeviceProtocolBeginSeal {
  constructor(deviceInfo, deviceProtocol, option) {
    super(deviceInfo, deviceProtocol, option);
  }

  async _fingerprintIdentify({success, complete, message, fail, timeout = -1} = {}) {
    return new Promise((resolve, reject) => {
      this.send.listen(this.deviceProtocol.serviceName.FINGERPRINT, {
        success, complete, timeout, message: msg => {
          const data = {
            params: {
              documentId: this.deviceInfo.documentId, sealid: this.deviceInfo.sealId, state: parseInt(msg.params[1])
            }
          };
          let result;
          if (data?.params?.sealid == this.option.sealId?.toString() && data?.params?.documentId == this.option.documentId?.toString()) {
            this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_IDENTIFY, result = {
              documentId: this.option.documentId,
              sealId: this.option.sealId,
              state: data.params?.state,
              success: data.params?.state === 1
            });

            message?.call(this, result);

            if (data.params?.state === 1) {
              resolve(result);
            }
          }
        }, fail: (err) => {
          fail?.call(this, err);
          reject(err);
        }
      });
    });
  }

  async _beginSeal({authentication = true, success, complete, message, fail, timeout} = {}) {
    this.option.leftTimes = this.deviceInfo.sealAuthentication === 1 ? 0 : this.deviceInfo.leftTimes;
    return this.send.service(this.deviceProtocol.serviceName.START_STAMP, [
      this.option.documentId,
      this.option.sealId,
      this.option.leftTimes,
      this.option.position ? this.option.position.map(s => s.toString().padLeft(3, "0")).join("") : "000",
      this.remote, authentication ? this.option.authentication : 2,
      this.option.pictureType
    ], {
      success, complete, message, fail, timeout
    }).catch(e => {
      throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT)
    });
  }


  async _equipmentLock({
                         sendEquipmentLock, success, complete, message, fail, timeout = -1
                       } = {}) {
    let isStart = true;
    const data = await sendEquipmentLock?.call(this);

    const documentRemoteId = data.documentRemoteId;


    let subscribeIndex;
    let clear = false;

    const onDocumentStateUploading = params => {
      params.documentRemoteId = documentRemoteId;
      this.deviceProtocol.emit(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, params);
    };

    /**
     *
     * @param type 1:正常结束  2:移动取消  3:app取消 4远程拒绝
     * @return {Promise<void>}
     */
    const clearMoveEvent = async (type = 3) => {
      if (subscribeIndex && !clear) {
        clear = true;
        if (type === 2) {
          this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_MOVE, {documentRemoteId});
        } else if (type === 3) {
          //this._remoteUnlockSend(documentRemoteId);
        }

        this.deviceProtocol.removeListener(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, onDocumentStateUploading);

        this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_UNLOCK, {documentRemoteId, type: type});
      }
    };

    this.once(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);

    const listen = async () => {
      while (isStart) {
        const data = await this._remoteResultSend(documentRemoteId);

        switch (data) {
          case 1:
          case 2:
            await this.examineResult(documentRemoteId, data, {mode: 1}).catch(e => {
              if (data === 2) {
                return null;
              }

              throw e;
            });

            await clearMoveEvent(data === 1 ? 1 : 4);
            break;
          case 3:
            await clearMoveEvent(3);
        }

        await this.delay(0.5);
      }
    }


    return {
      documentRemoteId, cancel: clearMoveEvent, listen: listen
    };
  }

  async _examineResult(documentRemoteId, state, {mode, success, complete, message, fail, timeout} = {}) {
    if (state === 1) {
      this.option.leftTimes = mode === 1 ? 1 : this.deviceInfo.leftTimes;
      return this.send.service(this.deviceProtocol.serviceName.START_STAMP,
              [
                this.option.documentId,
                this.option.sealId,
                this.option.leftTimes,
                this.option.position ? this.option.position.map(s => s.toString().padLeft(3, "0")).join("") : "000",
                this.option.sealAuthentication === 1 ? 4 : this.option.remote,
                2,
                this.option.pictureType,
                this.option.infrared,
                this.deviceInfo.operationId,
              ], {
                success, complete, message, fail, timeout
              });
    }
  }

  async _onSealEvent({success, complete, message, fail, timeout} = {}) {
    try {
      await this.send.listen(this.deviceProtocol.serviceName.REPLY_STAMP, {
        message: async (msg, reply) => {
          this.deviceInfo.leftTimes = this.deviceInfo.leftTimes - 1;
          const data = {
            // documentId~position~sealId~leftTimes~locateMode~gps~sealTime
            documentId: msg.params[1],
            sealId: msg.params[2],
            leftTimes: this.deviceInfo.leftTimes,
            sealTime: msg.params[4]
          };


          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 1});

          const result = await service.post(url.simpleUpload, {
            documentId: data.documentId,
            position: data.position,
            sealId: data.sealId,
            leftTimes: this.deviceInfo.leftTimes,
            gps: config.gps,
            sealTime: data.sealTime,
            number: this.deviceInfo.number,
            staffId: this.deviceInfo.staffId,
            coverId: this.deviceInfo.coverId
          })


          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 2});
          data.documentFileId = result.data.documentFileId;
          this.deviceProtocol.emit(DeviceEvent.DOCUMENT_STATE_UPLOADING, data);

          if (this.remote !== 3) {
            this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 3});
          }

          message?.call(this, data);

          if (this._mode === 1 && this._context.isExamine) {
            this.contextReset(true);
          }

          reply([]);
        }, fail, timeout
      });


      await this.send.listen(this.deviceProtocol.serviceName.WARN, {
        message: ({params}) => {

          if (params[1] === '2') {
            service.post(request.simpleTimeoutWarn, {
              documentId: this.deviceInfo.documentId,
              sealId: this.deviceInfo.sealId,
              number: this.deviceInfo.number,
              address: config.address
            });
          }

          this.deviceProtocol.emit(DeviceEvent.ABNORMAL_WARNING, {type: 11, warnType: params[1], time: new Date()});
        }
      });

      await success?.call(this);
      complete?.call(this);
    } catch (e) {
      complete?.call(this, null, e);
      throw WebError.code(deviceError.SYSTEM_ERROR, {
        error: e
      });
    }
  }

  async _endSeal({success, complete, message, fail, timeout} = {}) {
    if (this.option.leftTimes !== 0) {
      return this.send.cancelOperation({
        type: 1, success, complete, message, fail, timeout
      }).then(data => {
        this.send.disconnect(true);
        this.send.unsubscribe();
        this.mqttSend?.unsubscribe();

        return data;
      });
    }
  }
}

class SimpleBleDeviceProtocolOperation extends BleDeviceProtocolOperation {
  constructor(deviceInfo, deviceProtocol) {
    super(deviceInfo, deviceProtocol);
  }

  async firmwareUpgrade({data, success, complete, message, fail, timeout} = {}) {
    return this._firmwareUpgrade({data, success, complete, message, fail, timeout});
  }

  async _firmwareUpgrade({data, success, complete, message, fail, timeout}) {
    try {
      this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {step: 1, progress: 0});

      const response = await service.get(data.fullFirmwareLink, {
        responseType: "arraybuffer",
        onDownloadProgress: params => {
          this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {step: 1, progress: params.progress * 100});
        }
      });

      this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {step: 2, progress: 0});
      const pageSize = 256;
      const startData = new Date().getTime();
      let total;

      const contentLength = parseInt(response.headers["content-length"]);
      const buffer = Buffer.from(response.data);

      await this.deviceProtocol.sendPackage({
        buffer, size: pageSize, start: async (size, totalPageSize) => {
          total = totalPageSize;
          await this.send.service(this.deviceProtocol.serviceName.UPGRADE, [contentLength, totalPageSize, size, data.edition]);
        }, fn: (i, data, size) => {
          return new Promise(resolve => {
            setTimeout(async () => {
              await this.send.service(this.deviceProtocol.serviceName.PACKET, [data]).then(data => {
                console.log("进度", i, "/", total, new Date().getTime() - startData);

                this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {step: 2, progress: i / total * 100});
                return data;
              })
              resolve();
            }, 200);
          })
        }
      });
    } catch (e) {
      this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {
        step: -1,
        progress: 0,
        desc: "升级失败"
      });
      throw e;
    }
    const clear = {};

    return {
      autoReconnect: false,
      reconnect: () => {
        return new Promise((resolve, reject) => {
          this.send.listen(DeviceEvent.DISCONNECTED, {
            cacheTopic: clear,
            message: async () => {
              let success = false;
              const check = async (version) => {
                if (version === data.edition && !success) {
                  success = true;

                  await service.put(url.firmwareUpdate, {
                    number: this.deviceInfo.number,
                    step: 3,
                    version: data.edition
                  })

                  resolve();
                }
              }

              setTimeout(async () => {
                //等待正常协议回调
                await this.send.listen("RESTART", {
                  cacheTopic: clear, message: async ({params}) => {
                    await check(params[1]);
                  }
                });

                //连接成功后, 只等5s的升级回复
                const timeout = setTimeout(() => {
                  reject(WebError.code(deviceError.UPGRADE_WAIT_SUCCESS_TIMEOUT));
                }, 30000);

                //连接设备, 只等待5分钟
                await this.deviceProtocol.connect({timeout: 30, config: setConfig(this.deviceInfo, {})});
              }, 10000);
            }
          })
        }).then(data => {
          this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {step: 3, progress: 100});
          return data;
        }).catch(e => {
          this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {
            step: -1,
            progress: 0,
            desc: "升级失败"
          });
          throw e;
        }).finally(() => {
          this.send.unsubscribe(clear);
        })
      }
    }
  }

  async addInk({success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }


  async withInk({success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }


  async singing({state, success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }


  async deviceMaintain({success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

}


class BleSimpleCodec extends BleCodec {

  static codec = new BleSimpleCodec();

  encode(action, params, {reply = false, errorCode}) {
    if (!reply) {
      if (action === "PACKET" || action === "FEATUREPACKET") {
        return params[0];
      } else {
        const data = `|${action}|${(params && params.length > 0 ? params.join("~") : "~")}|#`;
        const msg = `/>|${data.length + 3 + data.length.toString().length}${data}`;
        return Buffer.from(msg);
      }
    } else {
      let code = 0;
      let codeMsg = "OK"

      if (errorCode) {
        code = errorCode.code;
        codeMsg = "FAIL";
      }

      params.unshift(code);
      params.unshift(codeMsg);

      const data = `|${action}|${(params && params.length > 0 ? params.join("~") : "~")}|#`;
      const msg = `/>|${data.length + 3 + data.length.toString().length}${data}`;
      return Buffer.from(msg);
    }
  }
}

export {SimpleDeviceProvide};